import { AfterViewInit, Component, DestroyRef, DOCUMENT, ElementRef, inject, input, signal, viewChild } from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { ClTrapFocus } from '@corelabs/angular/ui';
import { OverlayService } from '../overlay/overlay.service';

@Component({
    selector: 'app-modal',
    templateUrl: './modal.component.html',
    styleUrls: ['./modal.component.css'],

    host: {
        // Must sit on the host — the element the router outlet adds/removes — or they never fire.
        '[animate.enter]': "'opening'",
        '[animate.leave]': "'closing'",
        '[class.small]': "size() === 'small'",
        '[class.maximized]': 'maximized()',
        '(document:keydown.escape)': 'onEscape()',
    },
    imports: [RouterOutlet, TranslatePipe, ClTrapFocus],
})
export class ModalComponent implements AfterViewInit {
    // Bound from route data ({ size: 'small' }) via withComponentInputBinding.
    readonly size = input<'default' | 'small'>('default');

    /** Toggled by app-modal-actions in the page header; resets when the modal is replaced. */
    readonly maximized = signal(false);

    private readonly document = inject(DOCUMENT);
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly overlayService = inject(OverlayService);

    private readonly overlayId = this.overlayService.register();
    private readonly previouslyFocused = <HTMLElement | null>this.document.activeElement;

    private readonly dialog = viewChild.required<ElementRef<HTMLElement>>('dialog');

    constructor() {
        inject(DestroyRef).onDestroy(() => {
            this.overlayService.unregister(this.overlayId);
            this.previouslyFocused?.focus();
        });
    }

    ngAfterViewInit(): void {
        this.dialog().nativeElement.focus();
    }

    toggleMaximized(): void {
        this.maximized.update((maximized) => !maximized);
    }

    onEscape(): void {
        if (this.overlayService.isTop(this.overlayId)) {
            this.close();
        }
    }

    close(): void {
        this.router.navigate([{ outlets: { [this.route.outlet]: null } }], { relativeTo: this.route.parent });
    }
}
